import { useState } from "react";
import { ChevronDown, CheckCircle2, Edit3, XCircle, Minus } from "lucide-react";

const REVIEWS = [
  {
    id: "rv1", icon: "Assessment", title: "New Assessment Items · Biology 101, Chapter 5",
    sub: "Assessment Generator · 5 MCQs · Confidence: 94%", confidence: 94,
    project: "Biology Pilot", assignee: { initials: "JP", color: "#6941c6", name: "John" },
    status: "pending",
    output: { label: "AI Output · Sample MCQ", text: `<strong>Q: Which phase of mitosis involves chromosomes aligning at the cell's equatorial plate?</strong><br/><br/>A) Prophase &nbsp; B) <strong>Metaphase ✓</strong> &nbsp; C) Anaphase &nbsp; D) Telophase<br/><br/><em style="color:var(--foreground, #1a1a2e);opacity:0.4;font-size:11.5px">+ 4 more questions generated for this chapter</em>` },
    actions: [{ label: "Approve All 5", type: "approve" }, { label: "Edit", type: "edit" }, { label: "Reject", type: "reject" }],
  },
  {
    id: "rv2", icon: "Accessibility", title: "Accessibility Remediation · 30 Alt Text Fixes",
    sub: "A11y Remediator · Biology 101 · Confidence: 87%", confidence: 87,
    project: "Higher Ed · Q2 2026", assignee: { initials: "SA", color: "#1e8a4a", name: "Sarah A." },
    status: "pending",
    output: { label: "AI Output · Sample Alt Text", text: `<strong>Image:</strong> cell_division_fig4.png<br/><strong>Generated:</strong> "Diagram showing a eukaryotic cell in metaphase with chromosomes aligned along the metaphase plate, flanked by spindle fibers attached to kinetochores."<br/><br/><em style="color:var(--foreground, #1a1a2e);opacity:0.4;font-size:11.5px">⚠ 3 images flagged for human review · complex diagrams</em>` },
    actions: [{ label: "Approve 27", type: "approve" }, { label: "Review 3 Flagged", type: "edit" }, { label: "Reject", type: "reject" }],
  },
  {
    id: "rv3", icon: "Audio", title: "TTS Audio · Pronunciation Review Needed",
    sub: "TTS Generator · Chemistry Fundamentals · 3 words flagged", confidence: 0,
    project: "STEM Initiative", assignee: { initials: "MR", color: "#c8841a", name: "Marcus R." },
    status: "action",
    output: { label: "Pronunciation Issues", text: `<div style="margin-bottom:8px"><strong>"Stoichiometry"</strong> → AI reads: "Stoy-key-om-etry" <span style="color:#ef4444">✗</span></div><div style="margin-bottom:8px"><strong>"Prabhu"</strong> → AI reads: "Pra-boo" <span style="color:#ef4444">✗</span></div><div><strong>"Enthalpy"</strong> → AI reads: "En-thal-pee" <span style="color:#22c55e">✓</span></div>` },
    actions: [{ label: "Apply & Save to Dictionary", type: "approve" }, { label: "Adjust", type: "edit" }],
  },
  {
    id: "rv4", icon: "Path", title: 'Mastery Path · "Python for Data Science" (5 modules)',
    sub: "Mastery Path Builder · Cross-title extraction · Confidence: 91%", confidence: 91,
    project: "Higher Ed · Q2 2026", assignee: { initials: "DM", color: "#1a6fc4", name: "Diana M." },
    status: "pending",
    output: { label: "Generated Mastery Path", text: `<div style="margin-bottom:4px"><strong>Module 1:</strong> Python Basics · from "Intro to Python" Ch.1–3</div><div style="margin-bottom:4px"><strong>Module 2:</strong> Data Structures · from "Intro to Python" Ch.4–7</div><div style="margin-bottom:4px"><strong>Module 3:</strong> Pandas & Data Manipulation · from "Data Science Bootcamp"</div><div style="margin-bottom:4px"><strong>Module 4:</strong> Visualization · cross-extracted from 2 titles</div><div><strong>Module 5:</strong> Capstone Assessment · 20 questions across all modules</div>` },
    actions: [{ label: "Approve & Export", type: "approve" }, { label: "Reorder Modules", type: "edit" }, { label: "Reject", type: "reject" }],
  },
];

const statusBadge = {
  pending: "bg-amber-50 text-amber-600 border-amber-200",
  action: "bg-red-50 text-red-600 border-red-200",
  approved: "bg-emerald-50 text-emerald-600 border-emerald-200",
};
const statusLabel = { pending: "PENDING", action: "ACTION NEEDED", approved: "APPROVED" };

const confColor = (c) => c >= 90 ? "#22c55e" : c >= 80 ? "#f59e0b" : "#ef4444";

const btnStyle = {
  approve: "bg-emerald-600 text-white hover:bg-emerald-700",
  edit: "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100",
  reject: "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100",
};

export const Review = () => {
  const [expanded, setExpanded] = useState({});

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div className="flex flex-col h-full" data-testid="review-page">
      <div className="px-6 pt-5 pb-4 bg-white border-b border-border/40">
        <h2 className="font-heading font-bold text-lg text-foreground mb-4">Human-in-the-Loop Review</h2>

        {/* Stats */}
        <div className="flex gap-6">
          {[
            { val: "8", label: "Pending Review", color: "text-amber-500" },
            { val: "142", label: "Approved This Week", color: "text-emerald-600" },
            { val: "12", label: "Rejected", color: "text-red-500" },
            { val: "93%", label: "AI Approval Rate", color: "text-[#1a6fc4]" },
          ]?.map((s) => (
            <div key={s.label}>
              <p className={`text-2xl font-heading font-bold ${s.color}`}>{s.val}</p>
              <p className="text-[10px] text-foreground/40 font-semibold uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3" data-testid="review-list">
        {REVIEWS?.map((r) => (
          <div key={r.id} className="bg-white border border-border/60 rounded-2xl overflow-hidden" data-testid={`review-${r.id}`}>
            {/* Header */}
            <button
              onClick={() => toggle(r.id)}
              className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-foreground/[0.01]"
              data-testid={`toggle-${r.id}`}
            >
              <div className="w-9 h-9 rounded-xl bg-[#014d7d]/8 flex items-center justify-center shrink-0 text-sm">
                {r.icon === "Assessment" ? "📝" : r.icon === "Accessibility" ? "♿" : r.icon === "Audio" ? "🔊" : "🗺"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{r.title}</p>
                <p className="text-xs text-foreground/40 mt-0.5">{r.sub}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-foreground/[0.04] text-foreground/50">{r.project}</span>
                  <span className="flex items-center gap-1.5 text-[10px] text-foreground/40">
                    <div className="w-5 h-5 rounded-full text-[8px] font-bold text-white flex items-center justify-center" style={{ background: r.assignee.color }}>{r.assignee.initials}</div>
                    Assigned to {r.assignee.name}
                  </span>
                </div>
              </div>
              <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${statusBadge[r.status]}`}>{statusLabel[r.status]}</span>
              <ChevronDown className={`w-4 h-4 text-foreground/45 shrink-0 ${expanded[r.id] ? "rotate-180" : ""}`} style={{ transition: "transform 0.2s" }} />
            </button>

            {/* Expanded body */}
            {expanded[r.id] && (
              <div className="px-5 pb-5 border-t border-border/30 pt-4" data-testid={`body-${r.id}`}>
                {/* Confidence bar */}
                {r.confidence > 0 && (
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-[10px] font-semibold text-foreground/40 uppercase tracking-wider w-20">Confidence</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${r.confidence}%`, background: confColor(r.confidence) }} />
                    </div>
                    <span className="text-xs font-semibold" style={{ color: confColor(r.confidence) }}>{r.confidence}%</span>
                  </div>
                )}

                {/* AI Output */}
                <div className="bg-[#f8f9fb] rounded-xl border border-border/30 p-4 mb-4">
                  <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-2">{r.output.label}</p>
                  <div className="text-sm text-foreground/70 leading-relaxed" dangerouslySetInnerHTML={{ __html: r.output.text }} />
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {r.actions?.map((a) => (
                    <button key={a.label} className={`text-xs font-semibold px-4 py-2 rounded-xl flex items-center gap-1.5 ${btnStyle[a.type]}`} data-testid={`action-${a.type}-${r.id}`}>
                      {a.type === "approve" && <CheckCircle2 className="w-3.5 h-3.5" />}
                      {a.type === "edit" && <Edit3 className="w-3.5 h-3.5" />}
                      {a.type === "reject" && <XCircle className="w-3.5 h-3.5" />}
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
